import React, { Suspense, useCallback, useEffect, useState } from 'react';
import {
    applyEdgeChanges,
    applyNodeChanges,
    ReactFlow,
    NodeChange,
    EdgeChange,
    Connection,
    addEdge,
    SelectionMode,
    BackgroundVariant,
    Background,
    Panel,
    ReactFlowProvider,
    Controls,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { CustomNode } from '../CustomNode/CustomNode';
import { useSetDepartments } from './hooks/useSetDepartments';
import { useAddNode } from './hooks/useAddNode';
import { useAddEdges } from './hooks/useAddEdges';
import { useCreateAndConnect } from './hooks/useCreateAndConnect';
import { useRfInstance } from './hooks/useRfInstance';
import { useSaveFlow } from './hooks/useSaveFlow';
import { useRestoreFlow } from './hooks/useRestoreFlow';
import { PAN_ON_DRAG, Queries } from '../../../../constants/constants';
import { ModalContentWrapper } from '../../../shared/Modal/ModalContentWrapper/ModalContentWrapper';
import { ListGrid } from '../../../layout/ListGrid/ListGrid';
import { PanelLayout } from '../../../layout/Panel/Panel';
import { LoaderFallback } from '../../../shared/LoaderFallback/LoaderFallback';
import { getDepartmentNetwork } from './utils/GetDepartmetsNetwork';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { generateDepartmentNetwork } from './departmentNetwork.slice';
import { rootState } from '../../../../redux/store';
import { CurrentDepartment } from './CurrentDepartment';
import { Loader } from '../../../shared/Loader/Loader';
import { RelativeCenter } from '../../../layout/RelativeCenter/RelativeCenter';
import { fetchCommitsPending } from '../Commits/commits.slice';
import { Button } from '../../../ui/Button';
import { FlexCenter } from '../../../layout/FlexCenter/FlexCenter';
import { fetchStatusPending } from '../Status/status.slice';
import { useSendWebsocketMessage } from '../../WebSocket/hooks/useSendWebsocketMessage';
import { useWebSocket } from '../../WebSocket/hooks/useWebSocket';
import { TextCustom } from '../../../ui/TextCustom';
import { Spacing } from '../../../layout/Spacing/Spacing';
import { AvatarLogo } from '../../../shared/AvatarLogo/AvatarLogo';
import { ICommitHistoryRes, IWebSocketData } from '../../../../interface/interfaces';
import { ScrollY } from '../../../layout/Scroll/ScrollY';
import { SpacingCustom } from '../../../layout/Spacing/SpacingCustom';
import { FlexBetween } from '../../../layout/FlexBetween/FlexBetween';
import IconComponent from '../../../shared/IconComponents/IconComponent';
import { ExecButton } from '../../../ui/ExecButton/ExecButton';
import { Modal } from '../../Modal/Modal';

const customNodeType = { customNode: CustomNode };

const ViewportDepartments: React.FC = () => {
    const { socketRef } = useWebSocket('Viewport department');
    const { sendWebsocketMessage } = useSendWebsocketMessage(socketRef);

    const [pos, setPos] = useState({ x: 400, y: 200 });
    const dispatch = useAppDispatch();
    const { rfInstance, setRfInstance } = useRfInstance();
    const { departmentsNetworkData, departmentsNetworkLoading } = useAppSelector((state: rootState) => state.departments);
    const { userAndTokenData } = useAppSelector((state: rootState) => state.loginUserAndTokenData);

    useSetDepartments();

    const { inDepartment, outDepartment, rootDepartment } = useAppSelector((state: rootState) => state.arangeDepartments);
    const { nodes, setNodes, onAdd, clearNodes } = useAddNode(pos.x, pos.y, inDepartment, outDepartment, rootDepartment, sendWebsocketMessage);
    const { edges, setEdges, connectEdges, clearEdges } = useAddEdges();
    useCreateAndConnect(rootDepartment, inDepartment, outDepartment, onAdd, connectEdges, clearNodes, clearEdges);

    const { globalTrigger } = useAppSelector((state: rootState) => state.trigger);

    // const queryProjectId = useQuery(Queries.PROJECT_ID);

    const [dep, setDep] = useState('Robotik');

    // commits history
    const { commitHistoryData, commitHistoryError, commitHistoryLoading } = useAppSelector((state: rootState) => state.commitHistory);
    const { currentSourceDepartment, currentTargetDepartment } = useAppSelector((state: rootState) => state.sourceTargetDepartment);

    const { project } = useAppSelector((state: rootState) => state.project);

    const { onSave } = useSaveFlow(rfInstance, nodes);
    const { onRestore } = useRestoreFlow(setNodes, setEdges);

    useEffect(() => {
        dispatch(generateDepartmentNetwork(getDepartmentNetwork(departmentsNetworkData, dep)));
    }, [dep, departmentsNetworkData]);

    //Get neighbours commits
    useEffect(() => {
        if (userAndTokenData && rootDepartment.DepartmentId !== 0) {
            dispatch(fetchCommitsPending({ url: `project/${project?._id}/department/${rootDepartment.DepartmentId}/departmentCommit`, token: userAndTokenData?.token }));
            dispatch(fetchStatusPending({ url: `project/${project?._id}/department/${rootDepartment.DepartmentId}/departmentStatus`, token: userAndTokenData?.token }));
        }
    }, [dispatch, rootDepartment.DepartmentId, userAndTokenData, globalTrigger]);

    const onNodesChange = useCallback((changes: NodeChange[]) => setNodes((nds) => applyNodeChanges(changes, nds)), [setNodes]);
    const onEdgesChange = useCallback((changes: EdgeChange[]) => setEdges((eds) => applyEdgeChanges(changes, eds)), [setEdges]);
    const onConnect = useCallback((connection: Connection) => setEdges((eds) => addEdge({ ...connection, animated: true }, eds)), [setEdges]);

    const changeDepartments = (departmentName: string) => {
        setDep(departmentName);
    };

    const onOpenVersion = (fileName: string) => {
        const data: IWebSocketData = {
            defaultLocation: '',
            parameter: 'Commitordner oeffnen',
            data: [],
            department: '',
            projectID: project?._id || '',
            token: userAndTokenData?.token || '',
            filePath: fileName || '',
        };

        sendWebsocketMessage(JSON.stringify(data));
    };

    const commitHistoryJSX = commitHistoryData
        .slice()
        .reverse()
        .map((item: ICommitHistoryRes, idx: number) => {
            return (
                <PanelLayout borderIntensity={0.15} stretch key={idx} hasShadow={false} color="grey-superLight">
                    <Spacing marginSpacing="m" paddingSpacing="m">
                        <FlexCenter stretch position="start" direction="v">
                            <Spacing marginSpacing="none" paddingSpacing="m">
                                <AvatarLogo size="m" userName={item.User.split('.')[0]} userSurname={item.User.split('.')[1]} />
                            </Spacing>

                            <div style={{ width: '100%' }}>
                                <FlexCenter stretch gap={1} position="start">
                                    <FlexBetween>
                                        <TextCustom weight={600} color="dark">
                                            User:
                                        </TextCustom>
                                        <TextCustom color="dark">{item.User}</TextCustom>
                                    </FlexBetween>
                                    <FlexBetween>
                                        <TextCustom weight={600} color="dark">
                                            Version id:
                                        </TextCustom>
                                        <TextCustom color="dark">{item.CommitId}</TextCustom>
                                    </FlexBetween>
                                    <FlexCenter stretch position="start" gap={0.5}>
                                        <TextCustom weight={600} color="dark">
                                            Version text:
                                        </TextCustom>
                                        <PanelLayout stretch hasShadow={false}>
                                            <Spacing marginSpacing="none" paddingSpacing="m">
                                                <TextCustom color="dark">{item.CommitText}</TextCustom>
                                            </Spacing>
                                        </PanelLayout>
                                    </FlexCenter>
                                    <FlexCenter stretch position="start" gap={0.5}>
                                        <TextCustom weight={600} color="dark">
                                            FilePath:
                                        </TextCustom>
                                        <PanelLayout stretch hasShadow={false}>
                                            <Spacing marginSpacing="none" paddingSpacing="m">
                                                <TextCustom color="dark">{item.FilePath}</TextCustom>
                                            </Spacing>
                                        </PanelLayout>
                                    </FlexCenter>
                                    <FlexCenter stretch position="start" gap={0.5}>
                                        <TextCustom weight={600} color="dark">
                                            Content:
                                        </TextCustom>
                                        <PanelLayout stretch hasShadow={false}>
                                            <Spacing marginSpacing="none" paddingSpacing="m">
                                                {item.Content.map((contentItem: string, idx: number) => {
                                                    return (
                                                        <TextCustom key={idx} color="dark">
                                                            - {contentItem}
                                                        </TextCustom>
                                                    );
                                                })}
                                            </Spacing>
                                        </PanelLayout>
                                    </FlexCenter>
                                    <div style={{ width: '100%', borderBottom: '1px solid rgba(0,0,0,.1)' }}></div>
                                    <FlexBetween>
                                        <div></div>
                                        <SpacingCustom paddingTop={0.5}>
                                            <ExecButton size="s" label={'Open version'} iconName={'OpenCommit'} func={() => onOpenVersion(item.FilePath)} />
                                        </SpacingCustom>
                                    </FlexBetween>
                                </FlexCenter>
                            </div>
                        </FlexCenter>
                    </Spacing>
                </PanelLayout>
            );
        });

    return (
        <>
            {/* <WebCLI /> */}
            <Modal modalKey={"conflictDetailModal"} >
                <ModalContentWrapper title={'Conflict Details'}>
                </ModalContentWrapper>
            </Modal>


            <Modal modalKey={"commitHistoryModal"} >

                <ModalContentWrapper title={'Version history'}>
                    <div style={{ width: '700px', paddingRight: `${commitHistoryData.length === 1 ? '0rem' : '0rem'}` }}>
                        {commitHistoryLoading ? (
                            <Loader width={30} height={30} color="accent-green" label="Loading version history" />
                        ) : commitHistoryData.length === 0 ? (
                            <FlexCenter>
                                <IconComponent size={3} iconName={'Warning'} />
                                <TextCustom color="dark">Version history is empty.</TextCustom>
                            </FlexCenter>
                        ) : (
                            <>
                                <SpacingCustom paddingBottom={1} paddingTop={1}>
                                    <FlexBetween>
                                        <div style={{ display: 'flex', justifyContent: 'start', alignItems: 'center', gap: '.6rem' }}>
                                            <TextCustom color="dark">Sending from</TextCustom>
                                            <PanelLayout borderIntensity={1} colorBorder="accent-green" colorIntensity={0.3} hasShadow={false} color="accent-green">
                                                <Spacing marginSpacing="none" paddingSpacing="s">
                                                    <TextCustom weight={800} color="dark">
                                                        {currentSourceDepartment.DepartmentName}
                                                    </TextCustom>
                                                </Spacing>
                                            </PanelLayout>
                                            <TextCustom color="dark">to</TextCustom>
                                            <PanelLayout borderIntensity={0.3} colorBorder="dark" colorIntensity={1} hasShadow={false} color="grey-light">
                                                <Spacing marginSpacing="none" paddingSpacing="s">
                                                    <TextCustom weight={800} color="dark">
                                                        {currentTargetDepartment.DepartmentName}
                                                    </TextCustom>
                                                </Spacing>
                                            </PanelLayout>
                                        </div>
                                        <TextCustom color="grey-dark">
                                            ({commitHistoryData.length} commit{commitHistoryData.length !== 1 && 's'})
                                        </TextCustom>
                                    </FlexBetween>
                                </SpacingCustom>
                                <ScrollY height={336} isScrolling={commitHistoryData.length === 1 ? false : true}>
                                    <SpacingCustom paddingRight={commitHistoryData.length === 1 ? 0 : 0.5}>
                                        <FlexCenter stretch position="start">
                                            {commitHistoryJSX}
                                        </FlexCenter>
                                    </SpacingCustom>
                                </ScrollY>
                            </>
                        )}
                    </div>
                </ModalContentWrapper>
            </Modal>


            {departmentsNetworkLoading ? (
                <RelativeCenter>
                    <Loader width={30} height={30} color="accent-green" label={'Loading department network'} />
                </RelativeCenter>
            ) : (
                <ReactFlow
                    className="conflict-view__viewport"
                    nodes={nodes}
                    edges={edges}
                    fitView
                    // onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    onConnect={onConnect}
                    panOnScroll
                    panOnDrag={PAN_ON_DRAG}
                    selectionMode={SelectionMode.Partial}
                    onInit={setRfInstance}
                    fitViewOptions={{ padding: 2 }}
                    nodeTypes={customNodeType}
                >
                    <Panel position="top-right">
                        <FlexCenter position="start" direction="v" gap={0.5}>
                            <Button label={'VIBN'} role={'dark'} size={'small'} func={() => changeDepartments('VIBN')} />
                            <Button label={'OLP'} role={'dark'} size={'small'} func={() => changeDepartments('OLP')} />
                            <Button label={'SW'} role={'dark'} size={'small'} func={() => changeDepartments('SW-Konstruktion')} />
                            <Button label={'HW'} role={'dark'} size={'small'} func={() => changeDepartments('HW-Konstruktion')} />
                            <Button label={'IBN'} role={'dark'} size={'small'} func={() => changeDepartments('IBN')} />
                            <Button label={'Robotik'} role={'dark'} size={'small'} func={() => changeDepartments('Robotik')} />
                            <Button label={'Simulation'} role={'dark'} size={'small'} func={() => changeDepartments('Simulation')} />
                            <Button label={'Installation'} role={'dark'} size={'small'} func={() => changeDepartments('Installation')} />
                            <Button label={'Projektleitung'} role={'dark'} size={'small'} func={() => changeDepartments('Projektleitung')} />
                        </FlexCenter>
                    </Panel>
                    <div style={{ backgroundColor: 'white', display: 'inline-block', padding: '.5rem 1rem', position: 'absolute', bottom: '0px', right: '0px', zIndex: '10' }}>InVersum Viewport</div>
                    <Background className="conflict-view__background" color="#d7d7d7" variant={BackgroundVariant.Lines} gap={20} />
                    <Controls />
                    <CurrentDepartment label={'Current department: '} title={`${rootDepartment.Label}`} />
                </ReactFlow>
            )}
        </>
    );
};

const ViewportDepartmentsProvider: React.FC = () => {
    return (
        <PanelLayout>
            <ListGrid isSingle={true}>
                <Suspense fallback={<LoaderFallback color={'accent-green'} label={'Creating conflict panel'} />}>
                    <ReactFlowProvider>
                        <ViewportDepartments />
                    </ReactFlowProvider>
                </Suspense>
            </ListGrid>
        </PanelLayout>
    );
};

export default ViewportDepartmentsProvider;
